import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import './ReleaseCalendar.css';
import { getApiUrl } from '../utils/api';

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

/**
 * Modal component for releasing a parking slot
 * Rendered via portal outside the table structure to avoid z-index issues
 */
function ReleaseCalendarModal({ user, powerUserMode, msalInstance, slot, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [workingDays, setWorkingDays] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Calculate working days (Monday-Friday) in the selected date range
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (start > end) {
        setWorkingDays([]);
        return;
      }

      const days = [];
      const current = new Date(start);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
      }
      
      setWorkingDays(days);
    } else {
      setWorkingDays([]);
    }
  }, [startDate, endDate]);

  // Get today's date in YYYY-MM-DD format
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date as YYYY-MM-DD
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  };

  // Get weekday name
  const getWeekday = (date) => {
    const days = [
      t('weekdays.su'),
      t('weekdays.ma'),
      t('weekdays.ti'),
      t('weekdays.ke'),
      t('weekdays.to'),
      t('weekdays.pe'),
      t('weekdays.la')
    ];
    return days[date.getDay()];
  };

  const handleClose = () => {
    setError(null);
    setStartDate('');
    setEndDate('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!startDate || !endDate) {
      setError(t('releaseCalendar.dateRangeRequired'));
      return;
    }

    if (workingDays.length === 0) {
      setError(t('releaseCalendar.noWorkingDays'));
      return;
    }

    if (!slot || !slot.label) {
      setError(t('releaseCalendar.slotRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      // Create releases for this slot and all working days
      const releases = workingDays.map((date) => ({
        date: formatDateKey(date),
        slot_id: slot.label,
      }));

      // Call bulk release API
      const response = await fetch(getApiUrl('/api/slotReleases/bulk'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          releases,
          is_power_user: user?.isPowerUser && powerUserMode,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('releaseCalendar.failedToRelease'));
      }

      // Success - close modal and notify parent
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      console.error('Failed to create releases:', e);
      setError(e.message || t('releaseCalendar.failedToRelease'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="release-calendar-overlay" onClick={handleClose}>
      <div className="release-calendar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="release-calendar-header">
          <h2>
            {t('releaseCalendar.title')} - {slot.label}
            {slot.ev_charging === 1 && (
              <span className="ev-icon" title={t('parkingTable.evChargingAvailable')}>
                {' '}⚡
              </span>
            )}
          </h2>
          <button
            className="release-calendar-close"
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="release-calendar-form">
          <div className="form-group">
            <label htmlFor="startDate">
              {t('releaseCalendar.startDate')} <span className="required">*</span>
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={getTodayString()}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">
              {t('releaseCalendar.endDate')} <span className="required">*</span>
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || getTodayString()}
              required
            />
          </div>

          {workingDays.length > 0 && (
            <div className="form-group">
              <label>{t('releaseCalendar.workingDaysPreview')}</label>
              <div className="working-days-preview">
                {workingDays.slice(0, 10).map((date, idx) => (
                  <span key={idx} className="working-day-badge">
                    {formatDateDisplay(date)} ({getWeekday(date)})
                  </span>
                ))}
                {workingDays.length > 10 && (
                  <span className="working-day-badge more">
                    +{workingDays.length - 10} {t('releaseCalendar.moreDays')}
                  </span>
                )}
              </div>
              <small className="form-hint">
                {t('releaseCalendar.workingDaysCount', { count: workingDays.length })}
              </small>
            </div>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          {workingDays.length > 0 && (
            <div className="summary-info">
              {t('releaseCalendar.summary', {
                slot: slot.label,
                dayCount: workingDays.length,
                releaseCount: workingDays.length,
              })}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || workingDays.length === 0}
            >
              {isSubmitting ? t('releaseCalendar.releasing') : t('releaseCalendar.releaseSlotButton')}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="btn-cancel"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render modal via portal to document.body to avoid z-index issues with table headers
  return createPortal(modalContent, document.body);
}

export default ReleaseCalendarModal;
